import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { useThemePreferences } from '../theme/ThemeContext';
import { isDyslexiaTheme } from '../theme/theme';
import {
  Box, Drawer, AppBar, Toolbar, Typography, List, ListItemButton,
  ListItemIcon, ListItemText, IconButton, Avatar, Divider, Alert, Button,
  Stack, Badge, Card, TextField, Popover, InputAdornment, Chip,
  Dialog, DialogTitle, DialogContent, Table, TableBody, TableRow, TableCell,
  Snackbar,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Assignment as WorkIcon,
  CalendarMonth as CalendarIcon,
  TrendingUp as StrategyIcon,
  Image as ImageIcon,
  Psychology as ContextIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Menu as MenuIcon,
  AccessTime as FocusTimeIcon,
  Assessment as ReportsIcon,
  RecordVoiceOver as TranscriptIcon,
  Groups as EAWorkspaceIcon,
  SmartToy as PromptIcon,
  AdminPanelSettings as AdminIcon,
  Business as TenantsIcon,
  Inventory as ProductsIcon,
  Email as EmailIcon,
  BarChart as StatsIcon,
  Security as SecurityIcon,
  Payment as PaymentIcon,
  CorporateFare as OrgIcon,
  AccountCircle as AccountCircleIcon,
  Webhook as WebhookIcon,
  MenuBook as ReflectionsIcon,
  Lightbulb as LightbulbIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
  Notifications as NotificationsIcon,
  DoneAll as DoneAllIcon,
  Search as SearchIcon,
  FolderSpecial as FolderSpecialIcon,
} from '@mui/icons-material';
import { HubConnectionBuilder, HubConnection, HubConnectionState } from '@microsoft/signalr';
import { workItemsApi, dailyPlanApi, calendarSourcesApi, driversApi, authApi, notificationsApi } from '../api/services';
import type { WorkItem, AnnualStrategicDriver } from '../types/models';
import BrandMark from './BrandMark';
import { usePrincipal } from '../context/PrincipalContext';
import type { SelectChangeEvent } from '@mui/material';
import { Select, MenuItem, FormControl } from '@mui/material';

const DRAWER_WIDTH = 260;

// Client-side hint notifications (computed locally, not persisted)
interface LocalNotification {
  id: string;
  message: string;
  time: string;
  action?: { label: string; path: string };
}

// API-backed real-time notifications (persisted, pushed via SignalR)
interface ApiNotification {
  id: string;
  title: string;
  message?: string;
  type: string;
  entityType?: string;
  entityId?: string;
  isRead: boolean;
  createdAt: string;
}

interface SearchResult {
  type: 'Work Item' | 'Strategic Driver';
  title: string;
  subtitle?: string;
  path: string;
}

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  section?: string;
}

// Navigation for executives, EAs, and tenant admins
const tenantNavItems: NavItem[] = [
  // OPERATE
  { label: 'Dashboard', path: '/', icon: <DashboardIcon />, section: 'OPERATE' },
  { label: 'Focus Time', path: '/focus', icon: <FocusTimeIcon />, section: 'OPERATE' },
  { label: 'Reflections', path: '/reflections', icon: <ReflectionsIcon />, section: 'OPERATE' },
  { label: 'Work Items', path: '/work-items', icon: <WorkIcon />, section: 'MANAGE' },
  { label: 'Calendar', path: '/calendar', icon: <CalendarIcon />, section: 'MANAGE' },
  { label: 'Transcripts', path: '/transcripts', icon: <TranscriptIcon />, section: 'MANAGE' },
  { label: 'Strategic Drivers', path: '/strategy', icon: <StrategyIcon />, section: 'MANAGE' },
  { label: 'Projects', path: '/projects', icon: <FolderSpecialIcon />, section: 'MANAGE' },
  { label: 'Ideas', path: '/ideas', icon: <LightbulbIcon />, section: 'MANAGE' },
  { label: 'Image Capture', path: '/images', icon: <ImageIcon />, section: 'MANAGE' },
  { label: 'Context Engine', path: '/context', icon: <ContextIcon />, section: 'MANAGE' },
  { label: 'Principal Profile', path: '/principal-profile', icon: <AccountCircleIcon />, section: 'MANAGE' },
  // INSIGHT
  { label: 'Reports', path: '/reports', icon: <ReportsIcon />, section: 'INSIGHT' },
  // EA WORKSPACE (shown for EA roles only — handled below)
  { label: 'EA Workspace', path: '/ea-workspace', icon: <EAWorkspaceIcon />, section: 'EA WORKSPACE' },
  // CONFIGURE
  { label: 'Preferences', path: '/preferences', icon: <SettingsIcon />, section: 'CONFIGURE' },
  { label: 'Feature Requests', path: '/feature-requests', icon: <LightbulbIcon />, section: 'CONFIGURE' },
  { label: 'Prompt Management', path: '/prompt-management', icon: <PromptIcon />, section: 'CONFIGURE' },
  { label: 'Webhooks', path: '/webhooks', icon: <WebhookIcon />, section: 'CONFIGURE' },
  { label: 'Organisation', path: '/organisation', icon: <OrgIcon />, section: 'CONFIGURE' },
  { label: 'Admin', path: '/admin', icon: <AdminIcon />, section: 'CONFIGURE' },
  { label: 'Billing', path: '/billing', icon: <PaymentIcon />, section: 'CONFIGURE' },
];

// Navigation for platform administrators — completely different view
const platformNavItems: NavItem[] = [
  { label: 'Platform Overview', path: '/platform', icon: <StatsIcon />, section: 'PLATFORM' },
  { label: 'Tenants', path: '/platform/tenants', icon: <TenantsIcon />, section: 'PLATFORM' },
  { label: 'Billing Centre', path: '/platform/billing', icon: <PaymentIcon />, section: 'BILLING' },
  { label: 'Products & Pricing', path: '/platform/products', icon: <ProductsIcon />, section: 'BILLING' },
  { label: 'Email Settings', path: '/platform/email', icon: <EmailIcon />, section: 'BILLING' },
  { label: 'Security & Diagnostics', path: '/platform/security', icon: <SecurityIcon />, section: 'SYSTEM' },
];

export default function AppLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, ensureValidToken } = useAuth();
  const { preferences: themePrefs, setPreferences: setThemePrefs, resolvedMode } = useThemePreferences();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mfaBannerDismissed, setMfaBannerDismissed] = useState(false);

  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [localNotifications, setLocalNotifications] = useState<LocalNotification[]>([]);
  const [apiNotifications, setApiNotifications] = useState<ApiNotification[]>([]);
  const [tenantFeatures, setTenantFeatures] = useState<Record<string, boolean>>({});
  const hubConnectionRef = useRef<HubConnection | null>(null);

  // Search state
  const [globalSearch, setGlobalSearch] = useState('');
  const [searchAnchor, setSearchAnchor] = useState<HTMLElement | null>(null);
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchInputRef = React.useRef<HTMLInputElement>(null);

  // Keyboard shortcuts dialog
  const [showShortcutsDialog, setShowShortcutsDialog] = useState(false);

  // Toast notification state
  const [toast, setToast] = useState<{ message: string; action?: () => void; actionLabel?: string } | null>(null);

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      // Only fire when not in an input/textarea
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;

      if (e.key === 'n' && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        navigate('/work-items?action=create');
      }
      if (e.key === 't' && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        navigate('/');
      }
      if (e.key === 'c' && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        navigate('/calendar');
      }
      if (e.key === '?' && !e.ctrlKey) {
        e.preventDefault();
        setShowShortcutsDialog(true);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [navigate]);

  // Request notification permission on first load (non-blocking)
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      // Small delay so the prompt doesn't appear immediately on first visit
      const timer = setTimeout(() => {
        Notification.requestPermission();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, []);

  // Principal context for EA users
  const { activePrincipal, setActivePrincipal, clearPrincipal, principals, isActingOnBehalf } = usePrincipal();

  const handlePrincipalChange = useCallback((event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    if (value === '__my_view__') {
      clearPrincipal();
    } else {
      const selected = principals.find(p => p.id === value);
      if (selected) {
        setActivePrincipal(selected);
      }
    }
  }, [principals, setActivePrincipal, clearPrincipal]);

  const isPlatformAdmin = user?.role === 'PlatformAdmin';
  const isEaRole = user?.role === 'ExecutiveAssistant';
  const isAdmin = user?.role === 'Admin' || user?.role === 'TenantAdmin' || user?.isTenantAdmin;
  // Check MFA status from the stored login response flags
  const mfaSuggested = sessionStorage.getItem('mfaSuggested') === 'true';
  const mfaRequired = sessionStorage.getItem('mfaRequired') === 'true';
  const showMfaBanner = user && (mfaSuggested || mfaRequired) && !mfaBannerDismissed;

  // Load tenant features for nav visibility
  useEffect(() => {
    if (!user || isPlatformAdmin) return;
    authApi.me().then((me: any) => {
      if (me.features) setTenantFeatures(me.features);
    }).catch(() => {});
  }, [user, isPlatformAdmin]);

  // Force PlatformAdmin to MFA setup — block navigation to any other page
  useEffect(() => {
    if (isPlatformAdmin && mfaRequired && location.pathname !== '/mfa-setup') {
      navigate('/mfa-setup', { replace: true });
    }
  }, [isPlatformAdmin, mfaRequired, location.pathname, navigate]);

  // Compute client-side hint notifications on mount
  useEffect(() => {
    if (!user || isPlatformAdmin) return;
    const computeNotifications = async () => {
      const notifs: LocalNotification[] = [];
      const now = new Date();
      try {
        // Check for carry-forward items older than 3 days
        const workItems = await workItemsApi.getAll();
        const staleItems = workItems.filter(wi => {
          if (!wi.isCarryForward || wi.status === 'Done' || wi.status === 'Cancelled') return false;
          const orig = wi.originalDate ? new Date(wi.originalDate) : new Date(wi.createdAt);
          const daysDiff = Math.floor((now.getTime() - orig.getTime()) / (1000 * 60 * 60 * 24));
          return daysDiff >= 3;
        });
        if (staleItems.length > 0) {
          notifs.push({
            id: 'carry-forward-stale',
            message: `${staleItems.length} item${staleItems.length !== 1 ? 's have' : ' has'} been carried forward for 3+ days`,
            time: 'Review overdue items',
            action: { label: 'View Work Items', path: '/work-items' },
          });
        }

        // Check if today's plan has been started
        const today = now.toISOString().split('T')[0];
        try {
          const plan = await dailyPlanApi.getByDate(today);
          if (!plan || plan.status === 'Draft') {
            notifs.push({
              id: 'plan-not-started',
              message: "You haven't started your day yet",
              time: 'Start your day to get a planned schedule',
              action: { label: 'Go to Dashboard', path: '/' },
            });
          }
        } catch {
          // No plan for today
          notifs.push({
            id: 'plan-not-started',
            message: "You haven't started your day yet",
            time: 'Start your day to get a planned schedule',
            action: { label: 'Go to Dashboard', path: '/' },
          });
        }
      } catch {
        // API errors are non-critical for notifications
      }

      // MFA not enabled
      if (user && !user.mfaEnabled) {
        notifs.push({
          id: 'mfa-not-enabled',
          message: 'Secure your account with MFA',
          time: 'Multi-factor authentication adds an extra layer of security',
          action: { label: 'Set up MFA', path: '/mfa-setup' },
        });
      }

      // Calendar not connected
      try {
        const sources = await calendarSourcesApi.getAll();
        if (!sources || sources.length === 0) {
          notifs.push({
            id: 'calendar-not-connected',
            message: 'Connect your calendar for better planning',
            time: 'Calendar integration enables time-aware scheduling',
            action: { label: 'Go to Calendar', path: '/calendar' },
          });
        }
      } catch {
        // Ignore — may not have permission
      }

      setLocalNotifications(notifs);
    };
    computeNotifications();
  }, [user, isPlatformAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch API notifications and establish SignalR connection for real-time updates
  useEffect(() => {
    if (!user || isPlatformAdmin) return;

    // Load existing unread notifications from the API
    notificationsApi.getAll(false).then(data => {
      setApiNotifications(data);
    }).catch(() => {
      // Non-critical — real-time will still work
    });

    // Build SignalR connection
    const apiBaseUrl = (process.env.REACT_APP_API_URL || 'https://localhost:7001/api').replace(/\/api$/, '');
    const connection = new HubConnectionBuilder()
      .withUrl(`${apiBaseUrl}/hubs/notifications`, {
        accessTokenFactory: () => ensureValidToken(),
      })
      .withAutomaticReconnect()
      .build();

    hubConnectionRef.current = connection;

    // Listen for real-time notifications
    connection.on('NotificationReceived', (notification: ApiNotification) => {
      setApiNotifications(prev => [{ ...notification, isRead: false }, ...prev]);

      // Show browser notification when tab is not focused
      if (document.hidden && 'Notification' in window && Notification.permission === 'granted') {
        // Try to use the service worker for richer notifications
        if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
          navigator.serviceWorker.controller.postMessage({
            type: 'SHOW_NOTIFICATION',
            title: notification.title,
            body: notification.message || '',
            tag: `cadence-${notification.id}`,
          });
        } else {
          // Fallback to basic Notification API
          new Notification(notification.title, {
            body: notification.message || '',
            icon: '/logo192.png',
            tag: `cadence-${notification.id}`,
          });
        }
      }
    });

    // Listen for calendar sync events — dispatch to window so Dashboard/Calendar can refresh
    connection.on('CalendarSynced', () => {
      window.dispatchEvent(new CustomEvent('cadence:calendar-synced'));
    });

    connection.start().catch(err => {
      console.warn('SignalR connection failed:', err);
    });

    return () => {
      if (connection.state !== HubConnectionState.Disconnected) {
        connection.stop();
      }
      hubConnectionRef.current = null;
    };
  }, [user, isPlatformAdmin, ensureValidToken]);

  // Global search handler
  const handleGlobalSearch = useCallback(async (query: string, anchorEl: HTMLElement | null) => {
    if (!query.trim()) {
      setSearchResults([]);
      setSearchAnchor(null);
      return;
    }
    setSearchLoading(true);
    setSearchAnchor(anchorEl);
    const q = query.toLowerCase();
    const results: SearchResult[] = [];
    try {
      const [workItems, drivers] = await Promise.all([
        workItemsApi.getAll().catch(() => [] as WorkItem[]),
        driversApi.getAll().catch(() => [] as AnnualStrategicDriver[]),
      ]);
      workItems
        .filter(wi => wi.title.toLowerCase().includes(q) || wi.description?.toLowerCase().includes(q))
        .slice(0, 5)
        .forEach(wi => results.push({
          type: 'Work Item',
          title: wi.title,
          subtitle: `${wi.status} - ${wi.priority}`,
          path: '/work-items',
        }));
      drivers
        .filter(d => d.title.toLowerCase().includes(q) || d.description?.toLowerCase().includes(q))
        .slice(0, 3)
        .forEach(d => results.push({
          type: 'Strategic Driver',
          title: d.title,
          subtitle: d.description?.slice(0, 60),
          path: '/strategy',
        }));
    } catch {
      // Search is best-effort
    }
    setSearchResults(results);
    setSearchLoading(false);
  }, []);

  const unreadApiCount = apiNotifications.filter(n => !n.isRead).length;
  const notificationCount = localNotifications.length + unreadApiCount;

  // Mark a single API notification as read
  const handleMarkAsRead = useCallback(async (id: string) => {
    try {
      await notificationsApi.markAsRead(id);
      setApiNotifications(prev => prev.map(n => n.id === id ? { ...n, isRead: true } : n));
    } catch {
      // Best effort
    }
  }, []);

  // Mark all API notifications as read
  const handleMarkAllAsRead = useCallback(async () => {
    try {
      await notificationsApi.markAllAsRead();
      setApiNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
    } catch {
      // Best effort
    }
  }, []);

  const displayName = user?.displayName || user?.email || 'User';

  // Filter nav items based on role
  let navItems: NavItem[];
  if (isPlatformAdmin) {
    navItems = platformNavItems;
  } else {
    navItems = tenantNavItems.filter(item => {
      // EA Workspace: only show for ExecutiveAssistant role
      if (item.section === 'EA WORKSPACE' && !isEaRole) {
        return false;
      }
      // Principal Profile: show for EA role and admins, not pure Executives
      if (item.path === '/principal-profile' && !isEaRole && !isAdmin) {
        return false;
      }
      // Admin-only items: Prompt Management, Webhooks, Organisation, Admin, Billing
      if (item.path === '/prompt-management' && !isAdmin) {
        return false;
      }
      if (item.path === '/organisation' && !isAdmin) {
        return false;
      }
      if (item.path === '/admin' && !isAdmin) {
        return false;
      }
      if (item.path === '/billing' && !isAdmin) {
        return false;
      }
      if (item.path === '/webhooks' && !isAdmin) {
        return false;
      }
      // Hide Projects when feature is disabled for the tenant
      if (item.path === '/projects' && tenantFeatures.projectsEnabled === false) {
        return false;
      }
      return true;
    });
  }

  const productName = isPlatformAdmin ? 'Cadence Platform' : 'Cadence';
  const tagline = isPlatformAdmin ? 'Platform administration' : 'Executive orchestration platform';

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 2.5, pb: 1 }}>
        {/* Platform admin keeps a red mark to make the mode unambiguous at a
            glance. Regular users see the standard Cadence icon. */}
        {isPlatformAdmin ? (
          <Box display="flex" alignItems="center" gap={1}>
            <Box sx={{
              width: 28, height: 28, borderRadius: 1.5,
              background: 'linear-gradient(135deg, #DC2626 0%, #B91C1C 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 14, fontWeight: 700, color: '#fff',
            }}>
              C
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main', letterSpacing: '-0.02em', fontSize: '1.1rem' }}>
              {productName}
            </Typography>
          </Box>
        ) : (
          <BrandMark size="sm" label={productName} />
        )}
        <Typography variant="caption" color="text.secondary" sx={{ ml: 4.5 }}>
          {tagline}
        </Typography>
      </Box>
      <Divider sx={{ mx: 2, mt: 1 }} />
      <List sx={{ flex: 1, px: 1.5, py: 1 }}>
        {(() => {
          let lastSection = '';
          return navItems.map((item) => {
            const showSection = item.section && item.section !== lastSection;
            if (item.section) lastSection = item.section;
            return (
              <React.Fragment key={item.path + item.label}>
                {showSection && (
                  <Typography
                    variant="overline"
                    sx={{ display: 'block', px: 1.5, pt: 2, pb: 0.5, color: 'text.secondary', fontSize: '0.65rem', letterSpacing: '0.1em' }}
                  >
                    {item.section}
                  </Typography>
                )}
                <ListItemButton
                  selected={location.pathname === item.path}
                  onClick={() => { navigate(item.path); setMobileOpen(false); }}
                  sx={{
                    borderRadius: 2,
                    mb: 0.5,
                    '&.Mui-selected': {
                      bgcolor: isPlatformAdmin ? 'rgba(220, 38, 38, 0.12)' : 'rgba(108, 155, 255, 0.12)',
                      '& .MuiListItemIcon-root': { color: isPlatformAdmin ? 'error.main' : 'primary.main' },
                      '& .MuiListItemText-primary': { color: isPlatformAdmin ? 'error.main' : 'primary.main', fontWeight: 600 },
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: 'text.secondary' }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: '0.875rem' }} />
                </ListItemButton>
              </React.Fragment>
            );
          });
        })()}
      </List>
      <Divider sx={{ mx: 2 }} />
      <Box sx={{ px: 2, pb: 1, pt: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <IconButton
            size="small"
            onClick={() => {
              const nextMode = resolvedMode === 'dark' ? 'light' : 'dark';
              setThemePrefs({
                ...themePrefs,
                mode: nextMode,
              });
            }}
          >
            {resolvedMode === 'dark' ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
          </IconButton>
          <Typography variant="caption" color="text.secondary">
            {resolvedMode === 'dark' ? 'Dark' : 'Light'}
          </Typography>
        </Stack>
      </Box>
      <Divider sx={{ mx: 2 }} />
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Avatar sx={{
          width: 32, height: 32, fontSize: '0.875rem',
          bgcolor: isPlatformAdmin ? 'error.dark' : 'primary.dark',
        }}>
          {displayName.charAt(0)}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" noWrap sx={{ color: 'text.primary', fontWeight: 500 }}>
            {displayName}
          </Typography>
          <Typography variant="caption" noWrap sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
            {user?.role}
          </Typography>
        </Box>
        <IconButton size="small" onClick={logout} title="Sign out">
          <LogoutIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Mobile app bar */}
      <AppBar position="fixed" sx={{ display: { md: 'none' }, bgcolor: 'background.paper', boxShadow: 'none', borderBottom: 1, borderColor: 'divider' }}>
        <Toolbar>
          <IconButton edge="start" onClick={() => setMobileOpen(!mobileOpen)}>
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flex: 1, fontWeight: 700, color: isPlatformAdmin ? 'error.main' : 'primary.main' }}>
            {productName}
          </Typography>
          {isEaRole && principals.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 140, mr: 1 }}>
              <Select
                value={activePrincipal?.id || '__my_view__'}
                onChange={handlePrincipalChange}
                sx={{ fontSize: '0.75rem', bgcolor: isActingOnBehalf ? 'rgba(25, 118, 210, 0.08)' : 'transparent' }}
                renderValue={(value) => {
                  if (value === '__my_view__') return 'My View';
                  const p = principals.find(p => p.id === value);
                  return p ? p.name : 'My View';
                }}
              >
                <MenuItem value="__my_view__">
                  <Typography variant="body2" fontSize="0.8rem">My View</Typography>
                </MenuItem>
                <Divider />
                {principals.map(p => (
                  <MenuItem key={p.id} value={p.id}>
                    <Typography variant="body2" fontSize="0.8rem">{p.name}</Typography>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {!isPlatformAdmin && (
            <IconButton size="small" onClick={() => setNotificationsOpen(true)}>
              <Badge badgeContent={notificationCount} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>
          )}
        </Toolbar>
      </AppBar>

      {/* Sidebar */}
      <Box component="nav" sx={{ width: { md: DRAWER_WIDTH }, flexShrink: 0 }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': { width: DRAWER_WIDTH, bgcolor: 'background.paper' } }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{ display: { xs: 'none', md: 'block' }, '& .MuiDrawer-paper': { width: DRAWER_WIDTH, bgcolor: 'background.paper', borderRight: 1, borderColor: 'divider' } }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main content */}
      <Box component="main" sx={{
        flex: 1, p: { xs: 1.5, sm: 2, md: 3 }, pt: { xs: 8, md: 3 }, maxWidth: '100%', overflow: 'auto',
        ...(isDyslexiaTheme(themePrefs.fontFamily) && {
          // Apply max line width to prose content, not layout containers
          '& .MuiTypography-body1, & .MuiTypography-body2, & p': {
            maxWidth: themePrefs.fontFamily === 'dyslexia-hc' ? '65ch' : '68ch',
          },
          textAlign: 'left',
        }),
      }}>
        {/* Top bar: Search + Notifications (desktop) */}
        {!isPlatformAdmin && (
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 2, mb: 2 }}>
            <TextField
              size="small"
              placeholder="Search work items, drivers..."
              value={globalSearch}
              onChange={e => setGlobalSearch(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  handleGlobalSearch(globalSearch, e.currentTarget as HTMLElement);
                }
              }}
              inputRef={searchInputRef}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
              sx={{ maxWidth: 400, flex: 1 }}
            />
            <Box sx={{ flex: 1 }} />
            {/* Principal context switcher — EA users only */}
            {isEaRole && principals.length > 0 && (
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <Select
                  value={activePrincipal?.id || '__my_view__'}
                  onChange={handlePrincipalChange}
                  displayEmpty
                  sx={{
                    fontSize: '0.85rem',
                    bgcolor: isActingOnBehalf ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                    '& .MuiSelect-select': { display: 'flex', alignItems: 'center', gap: 1, py: 0.75 },
                  }}
                  renderValue={(value) => {
                    if (value === '__my_view__') return 'My View';
                    const p = principals.find(p => p.id === value);
                    return p ? `Viewing: ${p.name}` : 'My View';
                  }}
                >
                  <MenuItem value="__my_view__">
                    <Typography variant="body2" fontWeight={!activePrincipal ? 600 : 400}>
                      My View
                    </Typography>
                  </MenuItem>
                  <Divider />
                  {principals.map(p => (
                    <MenuItem key={p.id} value={p.id}>
                      <Box>
                        <Typography variant="body2" fontWeight={activePrincipal?.id === p.id ? 600 : 400}>
                          {p.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {p.email}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <IconButton size="small" onClick={() => setNotificationsOpen(true)}>
              <Badge badgeContent={notificationCount} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>
          </Box>
        )}

        {/* Search results popover */}
        <Popover
          open={Boolean(searchAnchor) && (searchResults.length > 0 || searchLoading)}
          anchorEl={searchAnchor}
          onClose={() => { setSearchAnchor(null); setSearchResults([]); }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          disableAutoFocus
          disableEnforceFocus
          sx={{ mt: 0.5 }}
        >
          <Box sx={{ width: 400, maxHeight: 400, overflow: 'auto', p: 2 }}>
            {searchLoading ? (
              <Typography color="text.secondary" variant="body2">Searching...</Typography>
            ) : searchResults.length === 0 ? (
              <Typography color="text.secondary" variant="body2">No results found</Typography>
            ) : (
              <>
                {(() => {
                  const grouped: Record<string, SearchResult[]> = {};
                  searchResults.forEach(r => {
                    if (!grouped[r.type]) grouped[r.type] = [];
                    grouped[r.type].push(r);
                  });
                  return Object.entries(grouped).map(([type, items]) => (
                    <Box key={type} sx={{ mb: 1.5 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>
                        {type}s ({items.length})
                      </Typography>
                      {items.map((item, i) => (
                        <Box
                          key={i}
                          onClick={() => { navigate(item.path); setSearchAnchor(null); setSearchResults([]); setGlobalSearch(''); }}
                          sx={{
                            p: 1, borderRadius: 1, cursor: 'pointer',
                            '&:hover': { bgcolor: 'action.hover' },
                          }}
                        >
                          <Typography variant="body2" fontWeight={500}>{item.title}</Typography>
                          {item.subtitle && (
                            <Typography variant="caption" color="text.secondary">{item.subtitle}</Typography>
                          )}
                        </Box>
                      ))}
                    </Box>
                  ));
                })()}
              </>
            )}
          </Box>
        </Popover>

        {/* Notifications drawer */}
        <Drawer anchor="right" open={notificationsOpen} onClose={() => setNotificationsOpen(false)}>
          <Box sx={{ width: 380, p: 2 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography variant="h6" fontWeight={600}>Notifications</Typography>
              {unreadApiCount > 0 && (
                <Button
                  size="small"
                  startIcon={<DoneAllIcon />}
                  onClick={handleMarkAllAsRead}
                  sx={{ textTransform: 'none' }}
                >
                  Mark all read
                </Button>
              )}
            </Box>

            {/* API-backed notifications (real-time, persisted) */}
            {apiNotifications.length > 0 && (
              <>
                {apiNotifications.map(n => {
                  const isCoaching = n.type === 'CoachingNudge';
                  return (
                  <Card
                    key={n.id}
                    sx={{
                      mb: 1, p: 1.5, cursor: 'pointer',
                      bgcolor: n.isRead ? 'transparent' : 'action.hover',
                      borderLeft: n.isRead ? 'none' : '3px solid',
                      borderColor: isCoaching ? 'info.main'
                        : n.type === 'Alert' || n.type === 'Overdue' ? 'error.main'
                        : n.type === 'Warning' || n.type === 'KpiAlert' ? 'warning.main'
                        : 'primary.main',
                    }}
                    onClick={() => {
                      if (!n.isRead) handleMarkAsRead(n.id);
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      {isCoaching && <LightbulbIcon sx={{ fontSize: 16, color: 'info.main' }} />}
                      <Chip
                        label={isCoaching ? 'Coaching' : n.type}
                        size="small"
                        color={
                          isCoaching ? 'info'
                            : n.type === 'Alert' || n.type === 'Overdue' ? 'error'
                            : n.type === 'Warning' || n.type === 'KpiAlert' ? 'warning'
                            : 'primary'
                        }
                        variant="outlined"
                        sx={{ fontSize: '0.65rem', height: 20 }}
                      />
                      {!n.isRead && (
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: isCoaching ? 'info.main' : 'primary.main', flexShrink: 0 }} />
                      )}
                    </Box>
                    <Typography variant="body2" fontWeight={n.isRead ? 400 : 600} sx={{ mt: 0.5 }}>
                      {n.title}
                    </Typography>
                    {n.message && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                        {n.message}
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
                      {new Date(n.createdAt).toLocaleString()}
                    </Typography>
                  </Card>
                  );
                })}
              </>
            )}

            {/* Client-side hint notifications */}
            {localNotifications.length > 0 && (
              <>
                {apiNotifications.length > 0 && <Divider sx={{ my: 1.5 }} />}
                <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  Suggestions
                </Typography>
                {localNotifications.map(n => (
                  <Card key={n.id} sx={{ mb: 1, p: 1.5, cursor: n.action ? 'pointer' : 'default' }}
                    onClick={() => {
                      if (n.action) { navigate(n.action.path); setNotificationsOpen(false); }
                    }}
                  >
                    <Typography variant="body2">{n.message}</Typography>
                    <Typography variant="caption" color="text.secondary">{n.time}</Typography>
                    {n.action && (
                      <Chip label={n.action.label} size="small" color="primary" variant="outlined" sx={{ mt: 0.5, display: 'block', width: 'fit-content' }} />
                    )}
                  </Card>
                ))}
              </>
            )}

            {apiNotifications.length === 0 && localNotifications.length === 0 && (
              <Typography color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>No notifications</Typography>
            )}
          </Box>
        </Drawer>

        {/* Principal delegation banner — shown when EA is acting on behalf of an executive */}
        {isActingOnBehalf && activePrincipal && (
          <Alert
            severity="info"
            sx={{
              mb: 2,
              borderRadius: 2,
              bgcolor: 'rgba(25, 118, 210, 0.08)',
              border: '1px solid',
              borderColor: 'info.main',
              '& .MuiAlert-message': { width: '100%' },
            }}
            action={
              <Button color="inherit" size="small" onClick={clearPrincipal}>
                Back to My View
              </Button>
            }
          >
            <Typography variant="body2" fontWeight={500}>
              Acting on behalf of <strong>{activePrincipal.name}</strong>. All changes will be applied to their account.
            </Typography>
          </Alert>
        )}

        {showMfaBanner && (
          <Alert
            severity={isPlatformAdmin || isAdmin ? 'warning' : 'info'}
            sx={{ mb: 2, borderRadius: 2 }}
            action={
              <Box display="flex" gap={1}>
                <Button color="inherit" size="small" onClick={() => navigate('/mfa-setup')}>
                  Set up MFA
                </Button>
                {!isPlatformAdmin && !isAdmin && (
                  <Button color="inherit" size="small" onClick={() => setMfaBannerDismissed(true)}>
                    Dismiss
                  </Button>
                )}
              </Box>
            }
          >
            {isPlatformAdmin || isAdmin
              ? 'Multi-factor authentication is required for administrator accounts. Set up MFA now.'
              : 'Secure your account — set up multi-factor authentication to protect your data.'
            }
          </Alert>
        )}
        <Outlet />

        {/* Keyboard shortcuts dialog */}
        <Dialog open={showShortcutsDialog} onClose={() => setShowShortcutsDialog(false)}>
          <DialogTitle>Keyboard Shortcuts</DialogTitle>
          <DialogContent>
            <Table size="small">
              <TableBody>
                <TableRow><TableCell><code>t</code></TableCell><TableCell>Go to Today/Dashboard</TableCell></TableRow>
                <TableRow><TableCell><code>c</code></TableCell><TableCell>Go to Calendar</TableCell></TableRow>
                <TableRow><TableCell><code>n</code></TableCell><TableCell>New Work Item</TableCell></TableRow>
                <TableRow><TableCell><code>?</code></TableCell><TableCell>Show this help</TableCell></TableRow>
              </TableBody>
            </Table>
          </DialogContent>
        </Dialog>

        {/* Toast notifications with undo support */}
        <Snackbar
          open={!!toast}
          autoHideDuration={5000}
          onClose={() => setToast(null)}
          message={toast?.message}
          action={toast?.action ? (
            <Button color="secondary" size="small" onClick={() => { toast.action!(); setToast(null); }}>
              {toast.actionLabel || 'Undo'}
            </Button>
          ) : undefined}
        />
      </Box>
    </Box>
  );
}
